import { create } from 'zustand';
import { collection, query, where, getCountFromServer, Timestamp, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '@services/firebase';
import { AdminStats, GrowthDataPoint } from '../types';

interface AdminState {
    pendingUsersCount: number;
    pendingContentCount: number;
    pendingMessagesCount: number;
    pendingPublicTradesCount: number;
    stats: AdminStats | null;
    loadingStats: boolean;

    setPendingUsersCount: (count: number) => void;
    setPendingContentCount: (count: number) => void;
    setPendingMessagesCount: (count: number) => void;
    setPendingPublicTradesCount: (count: number) => void;
    fetchStats: () => Promise<void>;
}

export const useAdminStore = create<AdminState>((set) => ({
    pendingUsersCount: 0,
    pendingContentCount: 0,
    pendingMessagesCount: 0,
    pendingPublicTradesCount: 0,
    stats: null,
    loadingStats: false,

    setPendingUsersCount: (count) => set({ pendingUsersCount: count }),
    setPendingContentCount: (count) => set({ pendingContentCount: count }),
    setPendingMessagesCount: (count) => set({ pendingMessagesCount: count }),
    setPendingPublicTradesCount: (count) => set({ pendingPublicTradesCount: count }),

    fetchStats: async () => {
        set({ loadingStats: true });
        try {
            const usersRef = collection(db, 'users');
            const tradesRef = collection(db, 'trades');
            const strategiesRef = collection(db, 'strategies');

            // Fetch growth data (last 7 days)
            const growth: GrowthDataPoint[] = [];
            const now = new Date();
            const growthPromises = [];

            for (let i = 6; i >= 0; i--) {
                const date = new Date(now);
                date.setDate(date.getDate() - i);
                date.setHours(23, 59, 59, 999);
                const ts = Timestamp.fromDate(date);

                growthPromises.push(
                    getCountFromServer(query(usersRef, where('createdAt', '<=', ts))),
                    getCountFromServer(query(tradesRef, where('createdAt', '<=', ts))),
                    getCountFromServer(query(strategiesRef, where('createdAt', '<=', ts)))
                );
            }

            const growthSnaps = await Promise.all(growthPromises);

            for (let i = 0; i <= 6; i++) {
                const label = i === 6 ? "Aujourd'hui" : `J-${6 - i}`;
                growth.push({
                    name: label,
                    users: growthSnaps[i * 3].data().count,
                    trades: growthSnaps[i * 3 + 1].data().count
                });
            }

            // Calculation of changes (comparing last 24h)
            const usersNow = growthSnaps[18].data().count;
            const users24h = growthSnaps[15].data().count;
            const tradesNow = growthSnaps[19].data().count;
            const trades24h = growthSnaps[16].data().count;
            const strategiesNow = growthSnaps[20].data().count;
            const strategies24h = growthSnaps[17].data().count;

            const calcChange = (now: number, prev: number) => {
                if (prev === 0) return now > 0 ? 100 : 0;
                return Math.round(((now - prev) / prev) * 100);
            };

            const changes = {
                users: calcChange(usersNow, users24h),
                trades: calcChange(tradesNow, trades24h),
                strategies: calcChange(strategiesNow, strategies24h),
                revenue: 5 // Simulated for now
            };

            const recentSignups = usersNow - users24h;

            // Fetch counts and settings for real pricing
            const [cancelledUsersSnap, activeUsersSnap, freeSnap, advancedSnap, proSnap, settingsSnap] = await Promise.all([
                getCountFromServer(query(usersRef, where('subscriptionStatus', '==', 'CANCELLED'))),
                getCountFromServer(query(usersRef, where('status', '==', 'ACTIVE'))),
                getCountFromServer(query(usersRef, where('subscriptionTier', '==', 'FREE'))),
                getCountFromServer(query(usersRef, where('subscriptionTier', '==', 'ADVANCED'))),
                getCountFromServer(query(usersRef, where('subscriptionTier', '==', 'PROFESSIONAL'))),
                getDoc(doc(db, 'config', 'site_settings'))
            ]);

            const siteSettings = settingsSnap.exists() ? settingsSnap.data() : null;
            const prices = siteSettings?.pricing || {
                advanced: { monthly: 29 },
                professional: { monthly: 59 }
            };

            const advancedCount = advancedSnap.data().count;
            const proCount = proSnap.data().count;
            const estimatedRevenue = (advancedCount * (prices.advanced.monthly || 29)) + (proCount * (prices.professional.monthly || 59));

            const totalCancelled = cancelledUsersSnap.data().count;
            const churnRate = usersNow > 0 ? Math.round((totalCancelled / usersNow) * 100) : 0;

            // Fetch real Top Content
            const topBlogsQuery = query(collection(db, 'blog_posts'), where('status', '==', 'PUBLISHED'), orderBy('views', 'desc'), limit(3));
            const topStratsQuery = query(collection(db, 'strategies'), where('isPublic', '==', true), orderBy('views', 'desc'), limit(3));

            const [topBlogsSnap, topStratsSnap] = await Promise.all([
                getDocs(topBlogsQuery),
                getDocs(topStratsQuery)
            ]);

            const topContent = [
                ...topBlogsSnap.docs.map(d => ({ title: d.data().title, views: d.data().views || 0, type: 'BLOG' as const })),
                ...topStratsSnap.docs.map(d => ({ title: d.data().name, views: d.data().views || 0, type: 'STRATEGY' as const }))
            ].sort((a, b) => b.views - a.views).slice(0, 4);

            // Fetch real Audience Distribution (Devices/Sources)
            const audienceSnap = await getDoc(doc(db, 'site_stats', 'audience'));
            const audienceData = audienceSnap.exists() ? audienceSnap.data() : null;

            const calcPercent = (val: number, total: number) => {
                if (total === 0) return 0;
                return Math.round((val / total) * 100);
            };

            const totalDeviceHits = audienceData ? (audienceData.devices.desktop + audienceData.devices.mobile + audienceData.devices.tablet) : 0;

            const stats: AdminStats = {
                totalUsers: usersNow,
                activeUsers: activeUsersSnap.data().count,
                totalTrades: tradesNow,
                totalStrategies: strategiesNow,
                totalRevenue: estimatedRevenue,
                usersByTier: {
                    free: freeSnap.data().count,
                    advanced: advancedSnap.data().count,
                    professional: proSnap.data().count
                },
                growth,
                changes,
                recentSignups,
                churnRate,
                calculatedAt: new Date(),
                audienceData: audienceData ? {
                    devices: [
                        { name: 'Desktop', value: calcPercent(audienceData.devices.desktop || 0, totalDeviceHits), color: '#6366f1' },
                        { name: 'Mobile', value: calcPercent(audienceData.devices.mobile || 0, totalDeviceHits), color: '#10b981' },
                        { name: 'Tablet', value: calcPercent(audienceData.devices.tablet || 0, totalDeviceHits), color: '#f59e0b' },
                    ],
                    sources: [
                        { name: 'Direct', value: audienceData.sources.direct || 0 },
                        { name: 'Organic SEO', value: audienceData.sources.organic || 0 },
                        { name: 'Social Media', value: audienceData.sources.social || 0 },
                        { name: 'Referral', value: audienceData.sources.referral || 0 },
                    ],
                    topContent: topContent.length > 0 ? topContent : [
                        { title: 'Comment gérer son Drawdown', views: 1240, type: 'BLOG' },
                        { title: 'Stratégie ICT Silver Bullet', views: 980, type: 'STRATEGY' },
                    ],
                    avgSessionTime: '12m 45s' // Still dummy for now
                } : {
                    // FALLBACK GLOBAL UNIQUEMENT SI BD VIDE
                    devices: [
                        { name: 'Desktop', value: 65, color: '#6366f1' },
                        { name: 'Mobile', value: 28, color: '#10b981' },
                        { name: 'Tablet', value: 7, color: '#f59e0b' },
                    ],
                    sources: [
                        { name: 'Direct', value: 450 },
                        { name: 'Organic SEO', value: 320 },
                        { name: 'Social Media', value: 180 },
                        { name: 'Referral', value: 110 },
                    ],
                    topContent: [
                        { title: 'Comment gérer son Drawdown', views: 1240, type: 'BLOG' },
                        { title: 'Stratégie ICT Silver Bullet', views: 980, type: 'STRATEGY' },
                        { title: 'Psychologie du Trader', views: 850, type: 'BLOG' },
                        { title: 'Scalping EURUSD M1', views: 720, type: 'STRATEGY' },
                    ],
                    avgSessionTime: '12m 45s'
                }
            };

            set({ stats, loadingStats: false });
        } catch (error: any) {
            console.error("Error fetching admin stats:", error);
            set({ loadingStats: false });
        }
    }
}));
